import { useEffect } from 'react';

const DicomPreloader = ({ 
  enabled = true, 
  delay = 2000, // Wait 2 seconds after app load before preloading
  onPreloaded,
  onError,
  verbose = false // Set to true to enable console logging
}) => {
  useEffect(() => {
    if (!enabled) return;

    const preloadDicomComponents = async () => {
      try {
        if (verbose) console.log('🔄 Starting DICOM viewer preload...');
        const startTime = performance.now();

        // Preload the main components in parallel
        const preloadPromises = [
          import('./SimpleDicomViewer'),
          import('@lunit/insight-viewer'),
          import('@lunit/insight-viewer/viewport')
        ];

        await Promise.all(preloadPromises);
        
        const endTime = performance.now();
        const loadTime = Math.round(endTime - startTime);
        
        if (verbose) console.log(`✅ DICOM viewer preloaded successfully in ${loadTime}ms`);
        
        if (onPreloaded) {
          onPreloaded({ loadTime });
        }

        // Store preload status in sessionStorage for the session
        sessionStorage.setItem('dicomViewerPreloaded', 'true');
        sessionStorage.setItem('dicomViewerPreloadTime', loadTime.toString());

      } catch (error) {
        if (verbose) console.warn('⚠️ Failed to preload DICOM viewer:', error);
        if (onError) {
          onError(error);
        }
      }
    };

    // Check if already preloaded in this session
    const isAlreadyPreloaded = sessionStorage.getItem('dicomViewerPreloaded') === 'true';
    
    if (!isAlreadyPreloaded) {
      const timer = setTimeout(preloadDicomComponents, delay);
      return () => clearTimeout(timer);
    } else {
      if (verbose) console.log('✅ DICOM viewer already preloaded in this session');
      if (onPreloaded) {
        const cachedLoadTime = parseInt(sessionStorage.getItem('dicomViewerPreloadTime') || '0');
        onPreloaded({ loadTime: cachedLoadTime, cached: true });
      }
    }
  }, [enabled, delay, onPreloaded, onError, verbose]);

  // This component doesn't render anything
  return null;
};

// Hook to check if DICOM viewer is preloaded
export const useDicomPreloadStatus = () => {
  const isPreloaded = sessionStorage.getItem('dicomViewerPreloaded') === 'true';
  const loadTime = parseInt(sessionStorage.getItem('dicomViewerPreloadTime') || '0');
  
  return {
    isPreloaded,
    loadTime,
    getOptimizationTips: () => {
      if (!isPreloaded) {
        return [
          'Enable DICOM preloader in your app root',
          'Consider using lazy loading for better performance'
        ];
      }
      return [
        `Preloaded in ${loadTime}ms - performance optimized`,
        'DICOM viewer should load much faster now'
      ];
    }
  };
};

export default DicomPreloader; 